import React from 'react';
import { ApprovalStep } from '../types/approvals.types';
import { Check, X, Clock, RefreshCw, User } from 'lucide-react';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

interface ApprovalTimelineProps {
  steps: ApprovalStep[];
}

export const ApprovalTimeline: React.FC<ApprovalTimelineProps> = ({ steps }) => {
  const sortedSteps = [...steps].sort((a, b) => a.step_order - b.step_order);

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <Check className="w-4 h-4 text-emerald-600" />;
      case 'rejected':
        return <X className="w-4 h-4 text-rose-600" />;
      case 'request_change':
        return <RefreshCw className="w-4 h-4 text-indigo-600" />;
      default:
        return <Clock className="w-4 h-4 text-amber-600" />;
    }
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-100 border-emerald-200';
      case 'rejected':
        return 'bg-rose-100 border-rose-200';
      case 'request_change':
        return 'bg-indigo-100 border-indigo-200';
      default:
        return 'bg-amber-100 border-amber-200';
    }
  };

  return (
    <div className="relative pl-8 space-y-8 before:absolute before:left-4 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
      {sortedSteps.map((step, index) => (
        <div key={step.id || index} className="relative">
          {/* Connector Node */}
          <div className={`absolute -left-8 w-8 h-8 rounded-full border-2 flex items-center justify-center z-10 ${getStepColor(step.status_code)}`}>
            {getStepIcon(step.status_code)}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 hover: transition-">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <h4 className="font-normal text-slate-900">
                    Bước {step.step_order}: {step.role_id || 'Người duyệt'}
                  </h4>
                  {step.action_at && (
                    <p className="text-xs text-slate-500">
                      {format(new Date(step.action_at), 'HH:mm - dd/MM/yyyy', { locale: vi })}
                    </p>
                  )}
                </div>
              </div>
              <span className={`text-xs font-normal px-2 py-0.5 rounded-md  tracking-wider ${
                step.status_code === 'approved' ? 'text-emerald-600 bg-emerald-50' :
                step.status_code === 'rejected' ? 'text-rose-600 bg-rose-50' :
                step.status_code === 'request_change' ? 'text-indigo-600 bg-indigo-50' :
                'text-amber-600 bg-amber-50'
              }`}>
                {step.status_code === 'approved' ? 'Đã duyệt' :
                 step.status_code === 'rejected' ? 'Từ chối' :
                 step.status_code === 'request_change' ? 'Yêu cầu sửa' :
                 'Chờ duyệt'}
              </span>
            </div>

            {step.comment && (
              <div className="mt-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg border-l-4 border-slate-300 italic">
                "{step.comment}"
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
